import React, { FC, ReactNode, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import isFunction from 'lodash/isFunction';
import { useSelector } from 'react-redux';
import Accordion from 'react-bootstrap/Accordion';

import { UiInnerNotSPALink } from 'src/components/UI/InnerNotSPALink';
import { MenuItemEnum, SidebarMenuItemType } from 'src/models/Partner/Sidebar';
import { UiOuterLink } from 'src/components/UI/OuterLink';
import { useBemHelper } from 'src/utils/hooks/useBemHelper';
import { IconChevronUp } from 'src/components/Icons/ChevronUp';
import { UiButton } from 'src/components/UI/Button';
import { dispatchShowTemplateGalleryModal } from 'src/store/commonPart';
import { canCreateCampaignFromTemplateSelector } from 'src/store/commonPart/selector';

type Props = {
  item: SidebarMenuItemType;
  selectedItemId: number | null;
  icon: ReactNode;
  disabled: boolean;
  isCurrentPageSPA: boolean;

  subItem?: boolean;
};

type ItemComponentProps = Omit<Props, 'selectedItemId'> & {
  selected: boolean;

  onClick?: () => void;
};

const ItemComponent: FC<ItemComponentProps> = ({
  selected,
  item: { readyAsSPA, url, title, openNewTab, testId },
  icon,
  subItem,
  disabled,
  onClick,
  isCurrentPageSPA,
}) => {
  const bemHelper = useBemHelper('sidebar-item');

  const modifiers = [];
  if (selected) {
    modifiers.push('active');
  }
  if (subItem) {
    modifiers.push('sub-item');
  }
  if (disabled) {
    modifiers.push('disabled');
  }

  const blockClass = bemHelper(modifiers);

  const content = () => (
    <>
      <div className={bemHelper('icon-wrapper')}>{icon}</div>
      <span className={bemHelper('text')}>{title}</span>
    </>
  );

  if (disabled) {
    return (
      <span className={blockClass} data-testid={testId}>
        {content()}
      </span>
    );
  }

  if (isFunction(onClick)) {
    return (
      <UiButton onClick={onClick} className={blockClass} dataTestId={testId}>
        {content()}
      </UiButton>
    );
  }

  if (readyAsSPA && isCurrentPageSPA) {
    return (
      <Link to={url} className={blockClass} data-testid={testId}>
        {content()}
      </Link>
    );
  }

  return openNewTab ? (
    <UiOuterLink className={blockClass} href={url} dataTest="leftSideMenu" dataTestId={testId}>
      {content()}
    </UiOuterLink>
  ) : (
    <UiInnerNotSPALink className={blockClass} href={url} dataTest="leftSideMenu" dataTestId={testId}>
      {content()}
    </UiInnerNotSPALink>
  );
};

export const SidebarMenuItem: FC<Props> = (props) => {
  const canCreateCampaignFromTemplate = useSelector(canCreateCampaignFromTemplateSelector);

  const bemHelper = useBemHelper('sidebar-item');

  const { item, icon, selectedItemId, disabled, isCurrentPageSPA } = props;

  const anySubItemActive = useMemo(() => {
    if (!item.items.length) {
      return false;
    }

    const subItemsIds = item.items.map((subItem) => subItem.id);

    return !!selectedItemId && subItemsIds.includes(selectedItemId);
  }, [item.items, selectedItemId]);

  const [open, setOpen] = useState(anySubItemActive);

  if (item.items.length) {
    const modifiers = [];
    if (anySubItemActive) {
      modifiers.push('active');
    }
    if (disabled) {
      modifiers.push('disabled');
    }

    return (
      <Accordion defaultActiveKey={anySubItemActive ? '0' : undefined} onSelect={(state) => setOpen(!!state)}>
        <Accordion.Toggle
          as="button"
          disabled={disabled}
          eventKey="0"
          className={bemHelper(modifiers)}
          data-testid={item.testId}
        >
          <div className={bemHelper('icon-wrapper')}>{icon}</div>
          <span className={bemHelper('text')}>{item.title}</span>
          <IconChevronUp className={bemHelper('icon-chevron', !open && ['rotate'])} />
        </Accordion.Toggle>
        <Accordion.Collapse eventKey="0">
          <>
            {item.items.map((subItem) => (
              <SidebarMenuItem
                key={subItem.id}
                item={subItem}
                icon={null}
                selectedItemId={selectedItemId}
                subItem
                disabled={disabled}
                isCurrentPageSPA={isCurrentPageSPA}
              />
            ))}
          </>
        </Accordion.Collapse>
      </Accordion>
    );
  }

  const handleClick =
    canCreateCampaignFromTemplate && item.id === MenuItemEnum.NewCampaign
      ? dispatchShowTemplateGalleryModal
      : undefined;

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <ItemComponent {...props} selected={item.id === selectedItemId} onClick={handleClick} />;
};
